import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import { INVOICE_TYPES } from '@/modules/Invoice/constants'
import { getInvoices } from '@/modules/Invoice/services'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
    getInvoiceListColumns,
    getInvoiceListFilterItems,
} from '../utils/invoice-list-config'
import { useEInvoiceAccess } from './use-e-invoice-access'
import { SIGNED_DOCUMENT_STATUS_FIELDS } from '../constants/signed-documents'

/**
 * Shared filters interface for Invoice tabs
 * Includes invoice_status to preserve filters when switching tabs
 */
interface InvoiceFilters {
    invoice_status: string[]
    contract_role: string[]
    contract_direction: string[]
    product_group_name: string[]
    account_manager_codes: string[]
    invoice_apply_startdate: string[]
    invoice_apply_startdate_to: string[]
    invoice_apply_enddate: string[]
    e_invoice_status: string[] // 'none' = chưa phát hành hoá đơn điện tử
}

/**
 * Business logic hook for Invoice Deposit List
 * Filters invoices with type 'deposit'
 */
export const useInvoiceDeposit = () => {
    const { t } = useTranslation(['invoice'])
    // Cột / filter "HĐ điện tử": chỉ nhóm Kế toán / BOM / BOD / Admin
    const { canView: canViewEInvoice } = useEInvoiceAccess()

    const {
        params,
        filters,
        queryParams,
        setParams,
        setFilter,
        searchInputProps,
    } = useUrlSearchParams<InvoiceFilters>({
        defaults: {
            sort_by: 'invoice_id',
            sort_order: 'desc',
            page_size: 20,
        },
        filterDefaults: {
            invoice_status: [],
            contract_role: [],
            contract_direction: [],
            product_group_name: [],
            account_manager_codes: [],
            invoice_apply_startdate: [],
            invoice_apply_startdate_to: [],
            invoice_apply_enddate: [],
            e_invoice_status: [],
        },
    })

    // Form for filter controls
    const { control } = useForm<InvoiceFilters>({
        defaultValues: filters,
    })

    // Build query params merging URL filters with tab-specific filters
    const apiParams = useMemo(() => {
        // Parse URL filters
        let urlFilters: Record<string, unknown> = {}
        if (queryParams.filters) {
            try {
                urlFilters = JSON.parse(atob(queryParams.filters))
            } catch {
                urlFilters = {}
            }
        }

        // Merge with tab-specific filters (deposit type)
        const mergedFilters = {
            ...urlFilters,
            // Tab này không có chứng từ ký số: bỏ filter giữ lại từ tab khác
            payment_request_status: [],
            reconciliation_statement_status: [],
            invoice_type: [INVOICE_TYPES.Deposit],
        }

        return {
            ...queryParams,
            filters: btoa(JSON.stringify(mergedFilters)),
        }
    }, [queryParams])

    const { data, renderState, isFetching } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.INVOICES, 'deposit', apiParams],
        queryFn: () => getInvoices(apiParams),
        placeholderData: true,
    })

    // Filter out service, invoice_exporter_code and signed document status columns for deposit tab
    // (hoá đơn nạp tiền không có chứng từ ký số)
    const columns = useMemo(
        () =>
            getInvoiceListColumns(t, { showEInvoice: canViewEInvoice }).filter(
                (col) =>
                    col.key !== 'service' &&
                    col.key !== 'invoice_exporter_code' &&
                    !SIGNED_DOCUMENT_STATUS_FIELDS.includes(col.key)
            ),
        [t, canViewEInvoice]
    )
    const filterItems = useMemo(
        () =>
            getInvoiceListFilterItems(t, setFilter, {
                showEInvoice: canViewEInvoice,
            }).filter(
                (item) => !SIGNED_DOCUMENT_STATUS_FIELDS.includes(item.name)
            ),
        [t, setFilter, canViewEInvoice]
    )

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )

    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )

    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    return {
        invoices: data?.results || [],
        pagination: data?.pagination,
        stateComponent: renderState(),
        isFetching,
        params,
        columns,
        // Filter support
        filters,
        control,
        setFilter,
        filterItems,
        searchInputProps,
        // Handlers
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    }
}
